import React, { useEffect, useMemo, useRef, useState } from "react";
import type { EiqQnaPanelProps } from "./eiq-qna-panel";
import { useQnaAppSync, QnaQuestion } from "../../hooks/useQnaAppSync";
import "./eiq-qna-panel.css";

type ModeratorTab = "OPEN" | "ANSWERED" | "DISMISSED";

const formatQnaTime = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatQnaTimeShort = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const EiqQnaPanelAppSync: React.FC<EiqQnaPanelProps> = ({
  webinarId,
  userId,
  userName,
  userEmail,
  role = "ATTENDEE",
  className,
  hideHeader,
}) => {
  const [draft, setDraft] = useState("");
  const [tab, setTab] = useState<ModeratorTab>("OPEN");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [composerQuestionId, setComposerQuestionId] = useState<string | null>(null);
  const [composerDraft, setComposerDraft] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);
  const [composerFocused, setComposerFocused] = useState(false);
  // Guard against double-fire of answerQuestion on rapid clicks.
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const markBusy = (id: string, on: boolean) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const handleAnswerLive = async (qId: string) => {
    if (busyIds.has(qId)) return;
    markBusy(qId, true);
    try {
      await answerLive(qId);
    } finally {
      markBusy(qId, false);
    }
  };

  const {
    questions,
    isConnected,
    error,
    askQuestion,
    answerLive,
    answerText,
    dismissQuestion,
    deleteQuestion,
    clearError,
  } = useQnaAppSync({ webinarId, userId, userName, userEmail, role });

  const isHost = role === "HOST" || role === "SPEAKER";

  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".eiq-qna-panel__q-menu") &&
        !target.closest(".eiq-qna-panel__q-menu-btn")
      ) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenuId]);

  const counts = useMemo(() => {
    const c = { OPEN: 0, ANSWERED: 0, DISMISSED: 0 };
    for (const q of questions) {
      if (q.status === "OPEN") c.OPEN += 1;
      else if (q.status === "ANSWERED") c.ANSWERED += 1;
      else if (q.status === "DISMISSED") c.DISMISSED += 1;
    }
    return c;
  }, [questions]);

  const visibleQuestions = useMemo(
    () => (isHost ? questions.filter((q) => q.status === tab) : questions),
    [isHost, questions, tab],
  );

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [visibleQuestions.length]);

  // Clear error after 5s
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleAsk = () => {
    const body = draft.trim();
    if (!body) return;
    askQuestion(body, false);
    setDraft("");
    setComposerFocused(false);
  };

  const handleCancelAsk = () => {
    setDraft("");
    setComposerFocused(false);
  };

  const openComposerFor = (questionId: string) => {
    setComposerQuestionId(questionId);
    setComposerDraft("");
  };
  const closeComposer = () => {
    setComposerQuestionId(null);
    setComposerDraft("");
  };
  const submitComposer = (questionId: string) => {
    const body = composerDraft.trim();
    if (!body) return;
    answerText(questionId, body);
    closeComposer();
  };

  return (
    <div className={`eiq-qna-panel ${className || ""}`}>
      {!hideHeader && (
        <div className="eiq-qna-panel__header">
          <span>Q&amp;A</span>
        </div>
      )}

      {error && <div className="eiq-qna-panel__error">{error}</div>}

      {/* Moderator tabs — HOST / SPEAKER only. */}
      {isHost && (
        <div className="eiq-qna-panel__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "OPEN"}
            className={`eiq-qna-panel__tab ${tab === "OPEN" ? "is-active" : ""}`}
            onClick={() => setTab("OPEN")}
          >
            Questions <span className="eiq-qna-panel__tab-count">{counts.OPEN}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "ANSWERED"}
            className={`eiq-qna-panel__tab ${tab === "ANSWERED" ? "is-active" : ""}`}
            onClick={() => setTab("ANSWERED")}
          >
            Answered <span className="eiq-qna-panel__tab-count">{counts.ANSWERED}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "DISMISSED"}
            className={`eiq-qna-panel__tab ${tab === "DISMISSED" ? "is-active" : ""}`}
            onClick={() => setTab("DISMISSED")}
          >
            Dismissed <span className="eiq-qna-panel__tab-count">{counts.DISMISSED}</span>
          </button>
        </div>
      )}

      <div className="eiq-qna-panel__list" ref={listRef}>
        {visibleQuestions.length === 0 && (
          <div className="eiq-qna-panel__empty">
            {isHost
              ? tab === "OPEN"
                ? "No open questions."
                : tab === "ANSWERED"
                ? "No answered questions yet."
                : "No dismissed questions."
              : "No questions yet — ask the first one!"}
          </div>
        )}

        {visibleQuestions.map((q: QnaQuestion) => {
          const qTime = formatQnaTime(q.createdAt);
          const qTimeShort = formatQnaTimeShort(q.createdAt);
          return (
          <div key={q.id} className="eiq-qna-panel__q">
            <div className="eiq-qna-panel__q-head">
              <span className="eiq-qna-panel__q-meta">
                <strong title={qTime}>{q.isAnonymous ? "Anonymous" : q.askerName}</strong>
                <span className="eiq-qna-panel__q-time" title={qTime}>{qTimeShort}</span>
              </span>
              {isHost && q.status !== "DELETED" && (
                <button
                  type="button"
                  aria-label="Question actions"
                  className="eiq-qna-panel__q-menu-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenMenuId(openMenuId === q.id ? null : q.id);
                  }}
                >
                  ⋮
                </button>
              )}
            </div>
            <div className="eiq-qna-panel__q-body" title={qTime}>{q.body}</div>

            <div className="eiq-qna-panel__q-actions">
              {isHost && q.status === "OPEN" && (
                <>
                  <button
                    type="button"
                    onClick={() => handleAnswerLive(q.id)}
                    disabled={busyIds.has(q.id)}
                  >
                    Answer live
                  </button>
                  <button type="button" onClick={() => openComposerFor(q.id)}>
                    Type answer
                  </button>
                </>
              )}

              {isHost && q.status === "ANSWERED" && (
                <button type="button" onClick={() => openComposerFor(q.id)}>
                  Reply
                </button>
              )}
            </div>

            {isHost && composerQuestionId === q.id && (
              <div className="eiq-qna-panel__q-composer">
                <textarea
                  autoFocus
                  rows={2}
                  placeholder={
                    q.status === "ANSWERED"
                      ? "Write a reply…"
                      : "Type your answer…"
                  }
                  value={composerDraft}
                  onChange={(e) => setComposerDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      submitComposer(q.id);
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      closeComposer();
                    }
                  }}
                />
                <div className="eiq-qna-panel__q-composer-actions">
                  <button
                    type="button"
                    className="is-ghost"
                    onClick={closeComposer}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => submitComposer(q.id)}
                    disabled={!composerDraft.trim()}
                  >
                    {q.status === "ANSWERED" ? "Reply" : "Send answer"}
                  </button>
                </div>
              </div>
            )}

            {openMenuId === q.id && (
              <div className="eiq-qna-panel__q-menu">
                {q.status !== "DISMISSED" && (
                  <button
                    type="button"
                    onClick={() => {
                      dismissQuestion(q.id);
                      setOpenMenuId(null);
                    }}
                  >
                    Dismiss
                  </button>
                )}
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => {
                    deleteQuestion(q.id);
                    setOpenMenuId(null);
                  }}
                >
                  Delete
                </button>
              </div>
            )}

            {q.answers?.map((a) => {
              const aTime = formatQnaTime(a.createdAt);
              const aTimeShort = formatQnaTimeShort(a.createdAt);
              return (
                <div key={a.id} className="eiq-qna-panel__answer">
                  <div className="eiq-qna-panel__answer-head">
                    <strong title={aTime}>{a.responderName}</strong>
                    <span className="eiq-qna-panel__answer-time" title={aTime}>
                      {aTimeShort}
                    </span>
                  </div>
                  <div className="eiq-qna-panel__answer-body">{a.body}</div>
                </div>
              );
            })}
          </div>
          );
        })}
      </div>

      {!isHost && (
        <div
          className={`eiq-qna-panel__composer ${
            composerFocused || draft ? "is-expanded" : ""
          }`}
        >
          <textarea
            placeholder="Type your question here…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => setComposerFocused(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleAsk();
              }
            }}
          />
          {(composerFocused || draft) && (
            <div className="eiq-qna-panel__composer-actions">
              <button
                type="button"
                className="is-ghost"
                onClick={handleCancelAsk}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAsk}
                disabled={!isConnected || !draft.trim()}
              >
                Send
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EiqQnaPanelAppSync;
